/**
 * SetupLadder: where a build's setup stands, as one short row of rungs.
 *
 * Since the toolchain install and the sandbox snapshots, a build can set up
 * for many minutes before its first turn, and the card read "Lining up.
 * Nothing has been recorded yet" the whole time (COREX-1291 on prod,
 * 2026-10-12). The session posts a beat each time setup moves on; this draws
 * them with the planning ladder's markup. It is read from those beats and
 * nothing else: a rung the build never reported is never drawn as done.
 *
 * Setup is over at the ready beat, or at the build's first event of its own
 * (a reattached session posts no ready beat), and the row then collapses to
 * how long setup took. Beats are matched by name, so one this bundle does not
 * know yet is ignored rather than breaking the row.
 */
import type { ReactNode } from "react";
import type { CodingEvent, WorkItemRunListEntry } from "@yagni/shared";
import { sinceLabel } from "./LiveStream.js";
import type { LadderRungState } from "./PlanningLadder.js";

export type SetupRungId = "sandbox" | "clone" | "install" | "checks" | "start";

export interface SetupRung {
  id: SetupRungId;
  word: string;
  state: LadderRungState;
  /** The small word under the rung: elapsed time, "reused", "stopped here". Null for none. */
  sub: string | null;
}

export interface SetupLadderState {
  rungs: SetupRung[];
  /** Setup is behind the build: the ready beat, or the build's own first event. */
  over: boolean;
  /** The first setup beat; null when the build posted none. */
  startedAt: number | null;
  /** When setup ended; null while it runs. */
  endedAt: number | null;
}

const RUNGS: ReadonlyArray<{ id: SetupRungId; word: string }> = [
  { id: "sandbox", word: "Sandbox" },
  { id: "clone", word: "Clone" },
  { id: "install", word: "Install" },
  { id: "checks", word: "Checks" },
  { id: "start", word: "Start" },
];

/** Which rung a beat puts the build on. `ready` ends setup and is handled apart. */
const BEAT_RUNG: Record<string, SetupRungId> = {
  sandbox: "sandbox",
  clone: "clone",
  fetch: "clone",
  pull_request: "clone",
  install: "install",
  install_reused: "install",
  snapshot: "install",
  checks: "checks",
  start: "start",
};

/** Kinds only the build itself records: once one lands, setup is behind it. */
const BUILD_KINDS = new Set<CodingEvent["kind"]>([
  "tool_call", "tool_result", "thinking", "stage", "completed", "plan_proposed", "plan_revised",
]);

function setupStepOf(event: CodingEvent): string | null {
  if (event.kind !== "message") return null;
  const step = (event as { setupStep?: unknown }).setupStep;
  return typeof step === "string" ? step : null;
}

/** The ladder as of `nowMs`. Once the build has ended (`live` false) nothing is "now". */
export function setupLadderOf(events: readonly CodingEvent[], nowMs: number, live = true): SetupLadderState {
  let current: number | null = null;
  let currentAt = 0;
  let startedAt: number | null = null;
  let endedAt: number | null = null;
  const beatsOn = new Map<number, Set<string>>();

  for (const event of events) {
    const step = setupStepOf(event);
    if (step === null) {
      if (startedAt !== null && endedAt === null && BUILD_KINDS.has(event.kind)) endedAt = event.at;
      continue;
    }
    if (endedAt !== null) continue;
    if (step === "ready") {
      startedAt ??= event.at;
      endedAt = event.at;
      continue;
    }
    const rungId = BEAT_RUNG[step];
    if (!rungId) continue;
    startedAt ??= event.at;
    const index = RUNGS.findIndex((rung) => rung.id === rungId);
    const beats = beatsOn.get(index) ?? new Set<string>();
    beats.add(step);
    beatsOn.set(index, beats);
    current = index;
    currentAt = event.at;
  }

  const over = endedAt !== null;
  const furthest = beatsOn.size > 0 ? Math.max(...beatsOn.keys()) : -1;
  const endedOn = !live && !over ? current : null;

  const rungs = RUNGS.map((rung, index): SetupRung => {
    const base = { id: rung.id, word: rung.word };
    const beats = beatsOn.get(index);
    const reused = beats !== undefined && [...beats].every((beat) => beat === "install_reused");
    if (over) return { ...base, state: beats ? "done" : "skipped", sub: reused ? "reused" : null };
    if (index === endedOn) return { ...base, state: "stopped", sub: "stopped here" };
    if (live && index === current) {
      const since = sinceLabel(currentAt, nowMs);
      return { ...base, state: "now", sub: reused ? `reused · ${since}` : since };
    }
    if (beats) return { ...base, state: "done", sub: reused ? "reused" : null };
    return { ...base, state: index < furthest ? "skipped" : "pending", sub: null };
  });

  return { rungs, over, startedAt, endedAt };
}

export interface SetupLadderProps {
  /** The run on the card. The ladder draws only for a build. */
  run: { purpose?: WorkItemRunListEntry["purpose"] | null } | null;
  events: readonly CodingEvent[];
  live: boolean;
  /** The clock the current rung counts against: now while live, the end once it stopped. */
  nowMs: number;
}

export function SetupLadder({ run, events, live, nowMs }: SetupLadderProps): ReactNode {
  if (run?.purpose !== "execution") return null;
  const { rungs, over, startedAt, endedAt } = setupLadderOf(events, nowMs, live);
  // A build from before the beats, or one that has not posted its first yet.
  if (startedAt === null) return null;
  if (over) {
    return (
      <div className="ls-setup-done" data-testid="setup-ladder-done">
        Set up in {sinceLabel(startedAt, endedAt ?? startedAt)}
      </div>
    );
  }
  return (
    <ol className="ls-ladder" data-testid="setup-ladder" aria-label="Setup steps">
      {rungs.map((rung) => (
        <li
          key={rung.id}
          className={`ls-rung is-${rung.state}`}
          data-rung={rung.id}
          data-state={rung.state}
          aria-current={rung.state === "now" ? "step" : undefined}
        >
          <span className="ls-rung-dot" aria-hidden />
          <span className="ls-rung-word">{rung.word}</span>
          {rung.sub && <span className="ls-rung-sub">{rung.sub}</span>}
        </li>
      ))}
    </ol>
  );
}
